<template>
  <Dialog
    title="Image Preview"
    :show-close-button="true"
    :show-save-button="false"
    :show-cancel-button="false"
    :show-footer="true"
    @close-event="handleClose"
    md
  >
    <template #modal-content>
      <div class="px-3 pt-3 d-flex justify-content-center">
        <div
          class="p-2 text-muted flex justify-center gap-2 w-max mx-auto bg-primary-light rounded-lg"
        >
          <MagnifyingGlassPlusIcon class="size-5" />
          Please hover over the images to zoom!
        </div>
      </div>
      <div class="grid grid-cols-2 justify-center gap-1 py-4 px-3">
        <div v-for="image in images" :key="image.url">
          <div class="text-sm font-semibold text-ink-soft mb-1">{{ image.label }}</div>
          <ZoomOnHover
            :image-src="storage_url + '/' + image.url"
            :img-zoom="storage_url + '/' + image.url"
            :scale="2"
          />
        </div>
      </div>
    </template>
    <template #footer>
      <div class="px-3 pb-3 d-flex justify-content-center">
        <button class="btn btn-dark text-white" @click="downloadImages(images, order_no)">
          Download Preview Images
        </button>
      </div>
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
import Dialog from '@/components/Shared/Dialog.vue';
import { storage_url } from '@/composables';
import { useOrderDetail } from '@/composables/useOrderDetail';
import { ActivityItem, FactoryProduct } from '@/models';
import { MagnifyingGlassPlusIcon } from '@heroicons/vue/24/outline';
import { computed } from 'vue';
import ZoomOnHover from './ZoomOnHover.vue';

interface Props {
  factory_product?: FactoryProduct;
  order_no: string;
  activity_item?: ActivityItem;
}
const props = defineProps<Props>();
const { downloadImages } = useOrderDetail();

const sideLabel = (url: string, index: number): string => {
  if (/inside_front/i.test(url)) return 'Inside front';
  if (/inside_back/i.test(url)) return 'Inside back';
  if (index === 0) return 'Front';
  if (index === 1) return 'Back';
  return `Image ${index + 1}`;
};

const images = computed(() => {
  const list: { url: string; label: string }[] = [];
  const fp = props.factory_product;

  if (props.activity_item?.activity_files?.length) {
    props.activity_item.activity_files.forEach((file, index) => {
      if (file?.url && file.url !== 'null') {
        list.push({ url: file.url, label: sideLabel(file.url, index) });
      }
    });
  } else if (fp) {
    if (fp.front_image) list.push({ url: fp.front_image, label: 'Front' });
    if (fp.back_image) list.push({ url: fp.back_image, label: 'Back' });
  }

  // Reversible products (AB#19678): older activity snapshots only carry the
  // outside pair. When the shown files are the product's own design previews
  // (URL match), append the inside pair from the factory_products snapshot;
  // newer snapshots already include them, so dedup by URL.
  if (fp && (fp.inside_front_image || fp.inside_back_image)) {
    const urls = new Set(list.map((image) => image.url));
    const isDesignPreviewPair = urls.has(fp.front_image) || urls.has(fp.back_image);
    if (isDesignPreviewPair) {
      if (fp.inside_front_image && !urls.has(fp.inside_front_image)) {
        list.push({ url: fp.inside_front_image, label: 'Inside front' });
      }
      if (fp.inside_back_image && !urls.has(fp.inside_back_image)) {
        list.push({ url: fp.inside_back_image, label: 'Inside back' });
      }
    }
  }

  return list;
});

const emits = defineEmits(['close']);
const handleClose = () => {
  emits('close');
};
</script>
<style lang="scss" scoped></style>
